import { z } from "zod/v4";
import { DateUtils } from "../../../primitive-utils";
import { LOCAL_DATE_TIME_FORMAT } from "../../../time";
import { REGEX_LOCAL_DATE_TIME } from "../base/time-regex";
import { DaySchema, HourSchema, MillisecondSchema, MinuteSchema, MonthSchema, SecondSchema, YearSchema, } from "../base/time-schema";
const LocalDateTimeSchema = z
    .object({
    ...YearSchema.shape,
    ...MonthSchema.shape,
    ...DaySchema.shape,
    ...HourSchema.shape,
    ...MinuteSchema.shape,
    ...SecondSchema.shape,
    ...MillisecondSchema.shape,
})
    .refine(({ year, month, day, hour, minute, second, millisecond }) => {
    const date = new Date(year, month - 1, day, hour, minute, second, millisecond);
    return (date.getFullYear() === year &&
        date.getMonth() + 1 === month &&
        date.getDate() === day &&
        date.getHours() === hour &&
        date.getMinutes() === minute &&
        date.getSeconds() === second &&
        date.getMilliseconds() === millisecond);
}, {
    error: "存在しない日時です。",
})
    .brand("Year")
    .brand("Month")
    .brand("Day")
    .brand("Hour")
    .brand("Minute")
    .brand("Second")
    .brand("Millisecond");
// ** LocalDateTime <-> Date 変換系 ** //
/** DateをJST解釈のLocalDateTimeに変換 */
const fromDateJST = (v) => {
    const jstDate = DateUtils.utcToJSTTime(v);
    return fromDate(jstDate);
};
/** LocalDateTimeをJST解釈のDateに変換 */
const toDateJST = (v) => {
    return DateUtils.JSTTimeToUtc(toDate(v));
};
/** その日の0:00(JST)時点のDateを返す */
const atStartOfDayJST = (v) => {
    return DateUtils.startOfDayJST(toDate(v));
};
/** その日の23:59(JST)時点のDateを返す */
const atEndOfDayJST = (v) => {
    return DateUtils.endOfDayJST(toDate(v));
};
/** private. TZは実行環境に依存する */
const toDate = ({ year, month, day, hour, minute, second, millisecond, }) => {
    return new Date(year, month - 1, day, hour, minute, second, millisecond);
};
/** private. TZは実行環境に依存する */
const fromDate = (d) => {
    return LocalDateTime.of({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        hour: d.getHours(),
        minute: d.getMinutes(),
        second: d.getSeconds(),
        millisecond: d.getMilliseconds(),
    });
};
// ** LocalDateTime <-> string 変換系 ** //
/** LocalDateTime -> string */
const format = (v, format = LOCAL_DATE_TIME_FORMAT) => {
    return DateUtils.format(toDate(v), format, true);
};
/**
 * 2020-01-01T10:00.000, 2020/01/01 10:10, 2020年01月01日(火) などの形式のLocalDateTimeを扱うえるようにするスキーマ
 */
const LocalDateTimeStringSchema = z
    .string()
    .regex(REGEX_LOCAL_DATE_TIME, { message: "日時の形式が不正です。" })
    .transform((val) => {
    const extractRegex = /\d+/g; // 数字を抽出
    const [year, month, day, hour, minute = 0, second = 0, millisecond = 0] = val.match(extractRegex)?.map(Number) ?? [];
    return { year, month, day, hour, minute, second, millisecond };
});
/** 形式チェックのみで、厳密な日付存在チェックを経ないparse */
const parseSoft = (v) => {
    return LocalDateTimeStringSchema.parse(v);
};
/** string => LocalDateTime */
const parse = (v) => {
    return LocalDateTime.schema.parse(parseSoft(v));
};
// ** LocalDateTimeの判定系 ** //
// USAGE: localDateTimeArray.sort(LocalDateTime.compere);
// 日の昇順 1/1, 1/2, 1/3... で並ぶ
const compare = (a, b) => {
    return toDate(a).getTime() - toDate(b).getTime();
};
// 日の降順 1/3,1/2,1/1... で並ぶ
const compareDesc = (a, b) => {
    return toDate(b).getTime() - toDate(a).getTime();
};
const equals = (a, b) => {
    return toDate(a).getTime() === toDate(b).getTime();
};
const isBefore = (a, b) => {
    return toDate(a).getTime() < toDate(b).getTime();
};
const isBeforeOrEqual = (a, b) => {
    return toDate(a).getTime() <= toDate(b).getTime();
};
// ** LocalDateTimeの操作系 ** //
const startOfMonth = (v) => {
    return LocalDateTime.of({
        ...v,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
    });
};
const endOfMonth = (v) => {
    return fromDate(DateUtils.endOfMonth(toDate({ ...v, day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 })));
};
export const LocalDateTime = {
    schema: LocalDateTimeSchema,
    stringSchema: LocalDateTimeStringSchema,
    TODAY: () => fromDateJST(new Date()),
    of(args) {
        const v = {
            ...args,
            hour: args.hour ?? 0,
            minute: args.minute ?? 0,
            second: args.second ?? 0,
            millisecond: args.millisecond ?? 0,
        };
        return LocalDateTime.schema.parse(v);
    },
    // ** LocalDateTime <-> Date 変換系 ** //
    toDateJST,
    atStartOfDayJST,
    atEndOfDayJST,
    fromDateJST,
    // ** LocalDateTime <-> string 変換系 ** //
    format,
    parseSoft,
    parse,
    // ** LocalDateTimeの判定系 ** //
    compare,
    compareDesc,
    equals,
    isBefore,
    isBeforeOrEqual,
    // ** LocalDateTimeの操作系 ** //
    startOfMonth,
    endOfMonth,
};
//# sourceMappingURL=local-date-time.js.map